import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {useOperatorInformation} from "../../../../../../hooks/use-operator-information";

/**
 * @typedef {Object} TransferOperatorsController
 * @property {(operatorId: string) => string} getOperatorName - Returns the cached operator name, falling back to the id.
 * @property {(operatorId: string) => void} resolveOperator - Ensures the operator's info is fetched and cached.
 * @property {Record<string, {name?: string, loading?: boolean}>} operators - The current operator cache.
 */

/** @type {React.Context<TransferOperatorsController>} */
const TransferOperatorsContext = createContext({
    getOperatorName: (operatorId) => operatorId,
    resolveOperator: () => undefined,
    operators: {},
});

/**
 * Builds an operator's display name from their info.
 */
const buildOperatorName = (info) => [info?.firstName, info?.lastName].filter(Boolean).join(' ').trim();

/**
 * Provides a cache of operator id -> display name for the transfer information view.
 * The current operator is resolved from the operator-information context without an API call,
 * and every other operator is fetched once and cached for the lifetime of the view.
 */
const TransferOperatorsProvider = ({children}) => {
    const isMounted = useIsMounted();
    const currentOperator = useOperatorInformation();
    const [operators, setOperators] = useState({});
    const cacheRef = useRef({});
    const pendingRef = useRef(new Set());

    /**
     * Fetches and caches the operator's info if it has not been resolved yet.
     * Skips the api call for the current operator and for already cached/in-flight operators.
     */
    const resolveOperator = useCallback(async (operatorId) => {
        if (!operatorId || currentOperator?.id === operatorId) {
            return;
        }
        if (cacheRef.current[operatorId] || pendingRef.current.has(operatorId)) {
            return;
        }
        pendingRef.current.add(operatorId);
        setOperators((prevState) => ({...prevState, [operatorId]: {loading: true}}));
        const response = await Api.getOperatorById(operatorId);
        pendingRef.current.delete(operatorId);
        if (!isMounted()) {
            return;
        }
        const name = buildOperatorName(response?.data) || operatorId;
        cacheRef.current[operatorId] = {name};
        setOperators((prevState) => ({...prevState, [operatorId]: {name, loading: false}}));
    }, [currentOperator?.id, isMounted]);

    /**
     * Returns the display name for the given operator id, falling back to the id itself.
     */
    const getOperatorName = useCallback((operatorId) => {
        if (!operatorId) {
            return '';
        }
        if (currentOperator?.id === operatorId) {
            return currentOperator?.name ? `${currentOperator.name} (You)` : operatorId;
        }
        return operators[operatorId]?.name ?? operatorId;
    }, [currentOperator?.id, currentOperator?.name, operators]);

    const controller = useMemo(() => ({
        getOperatorName,
        resolveOperator,
        operators,
    }), [getOperatorName, operators, resolveOperator]);

    return (
        <TransferOperatorsContext.Provider value={controller}>
            {children}
        </TransferOperatorsContext.Provider>
    );
};

export default TransferOperatorsProvider;

export const useTransferOperators = () => useContext(TransferOperatorsContext);
